import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { getAccessToken, getRefreshToken } from "@/lib/auth";
import type { AdminEnvironment } from "@/lib/environment";
import { EnvironmentBadge } from "./EnvironmentBadge";
import { ProdConfirmModal } from "./ProdConfirmModal";

type StageCode = "pre" | "prod";

const STAGE_LABEL: Record<StageCode, string> = {
  pre: "预发环境",
  prod: "生产环境",
};

const REGION_FLAG: Record<string, string> = {
  cn: "🇨🇳",
  hk: "🇭🇰",
  jp: "🇯🇵",
};

export function EnvironmentSwitcher() {
  const auth = useAdminAuth();
  const envOptions = useMemo(() => auth.envOptions ?? [], [auth.envOptions]);
  const { currentEnv, setCurrentEnv } = auth;
  const [open, setOpen] = useState(false);
  const [selectedStage, setSelectedStage] = useState<StageCode | null>(
    (currentEnv?.stage as StageCode | undefined) ?? null
  );
  const [pendingProd, setPendingProd] = useState<AdminEnvironment | null>(null);
  const [switching, setSwitching] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setSelectedStage((currentEnv?.stage as StageCode | undefined) ?? null);
    }
  }, [open, currentEnv?.stage]);

  useEffect(() => {
    if (!open) return;
    function handleKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open]);

  const stages = useMemo<StageCode[]>(() => {
    const set = new Set(envOptions.map((e) => e.stage as StageCode));
    return (["pre", "prod"] as const).filter((s) => set.has(s));
  }, [envOptions]);

  const regionsInStage = useMemo(() => {
    if (!selectedStage) return [];
    return envOptions.filter((e) => e.stage === selectedStage);
  }, [envOptions, selectedStage]);

  const switchTo = async (environment: AdminEnvironment) => {
    const target = environment.admin_base_url?.trim();
    if (target) {
      try {
        const targetOrigin = new URL(target).origin;
        if (targetOrigin !== window.location.origin) {
          // 跨域跳转：携带当前 token，目标域无独立 localStorage，不传则必须重新登录
          const url = new URL(target);
          const at = getAccessToken();
          const rt = getRefreshToken();
          if (at) url.searchParams.set("_at", at);
          if (rt) url.searchParams.set("_rt", rt);
          window.location.assign(url.toString());
          return;
        }
      } catch {
        // admin_base_url 解析失败，降级走 API 切换
      }
    }

    setSwitching(true);
    try {
      const res = await api.post("/quantum/business/env/current", {
        env_id: environment.id,
      });
      setCurrentEnv(res.data.data.current_env);
      setOpen(false);
    } finally {
      setSwitching(false);
      setPendingProd(null);
    }
  };

  const handleSelectRegion = (environment: AdminEnvironment) => {
    if (environment.id === currentEnv?.id) {
      setOpen(false);
      return;
    }
    if (environment.is_prod) {
      setPendingProd(environment);
      return;
    }
    void switchTo(environment);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex items-center gap-2 rounded-xl hover:bg-surface px-1.5 py-1 transition-colors"
      >
        <EnvironmentBadge environment={currentEnv} />
        <i className={`ri-arrow-down-s-line text-text-muted transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            ref={panelRef}
            className="absolute right-0 top-full z-50 mt-2 w-[360px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-surface-border bg-white shadow-xl"
          >
            <div className="flex items-center justify-between border-b border-surface-border px-5 py-3.5">
              <h3 className="text-sm font-semibold text-text-primary">选择管理环境</h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex h-7 w-7 items-center justify-center rounded-md text-text-secondary transition-colors hover:bg-surface"
              >
                <i className="ri-close-line" />
              </button>
            </div>

            {currentEnv && (
              <div
                className={`flex items-center gap-2 border-b border-surface-border px-5 py-3 text-sm ${
                  currentEnv.is_prod
                    ? "bg-status-danger/10 text-status-danger"
                    : "bg-status-warning/10 text-status-warning"
                }`}
              >
                <i className="ri-map-pin-line" />
                <span className="font-medium">当前正在管理：{currentEnv.display_name}</span>
              </div>
            )}

            {envOptions.length === 0 ? (
              <div className="px-5 py-8 text-center text-sm text-text-muted">暂无可用环境</div>
            ) : (
              <div className="space-y-5 p-5">
                <div>
                  <h4 className="mb-2.5 text-xs font-medium uppercase tracking-wide text-text-secondary">
                    环境阶段
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {stages.map((stage) => {
                      const isSelected = selectedStage === stage;
                      const isProd = stage === "prod";
                      return (
                        <button
                          key={stage}
                          type="button"
                          onClick={() => setSelectedStage(stage)}
                          className={`cursor-pointer whitespace-nowrap rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                            isSelected
                              ? isProd
                                ? "border-status-danger/30 bg-status-danger/10 text-status-danger"
                                : "border-brand/30 bg-brand/10 text-brand"
                              : isProd
                                ? "border-status-danger/20 bg-status-danger/5 text-status-danger hover:bg-status-danger/10"
                                : "border-surface-border bg-surface text-text-secondary hover:border-brand/30"
                          }`}
                        >
                          {isProd && <i className="ri-error-warning-line mr-1" />}
                          {STAGE_LABEL[stage]}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <div className="mb-2.5 flex items-center justify-between">
                    <h4 className="text-xs font-medium uppercase tracking-wide text-text-secondary">
                      机房 / 地域
                    </h4>
                    {!selectedStage && (
                      <span className="text-[11px] text-text-muted">请先选择环境阶段</span>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {selectedStage ? (
                      regionsInStage.length === 0 ? (
                        <span className="py-1 text-sm text-text-muted">该阶段下无可选机房</span>
                      ) : (
                        regionsInStage.map((env) => {
                          const isSelected = currentEnv?.id === env.id;
                          const flag = REGION_FLAG[env.region_code];
                          return (
                            <button
                              key={env.id}
                              type="button"
                              disabled={switching}
                              onClick={() => handleSelectRegion(env)}
                              className={`cursor-pointer whitespace-nowrap rounded-lg border px-3 py-1.5 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                                isSelected
                                  ? "border-brand/30 bg-brand/10 text-brand"
                                  : "border-surface-border bg-surface text-text-secondary hover:border-brand/30"
                              }`}
                            >
                              {flag && <span className="mr-1">{flag}</span>}
                              {env.region_name}
                            </button>
                          );
                        })
                      )
                    ) : (
                      <span className="py-1 text-sm text-text-muted">—</span>
                    )}
                  </div>

                  {selectedStage && regionsInStage.length > 0 && (
                    <p className="mt-2 text-[11px] text-text-muted">
                      该阶段下共有 {regionsInStage.length} 个可用机房
                    </p>
                  )}
                </div>

                <div className="rounded-xl bg-surface p-3.5 text-sm">
                  <div className="mb-1 text-text-secondary">当前选择</div>
                  <div className="font-medium text-text-primary">
                    {currentEnv ? (
                      currentEnv.display_name
                    ) : (
                      <span className="text-text-muted">请选择环境阶段和机房</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {pendingProd && (
        <ProdConfirmModal
          environment={pendingProd}
          onCancel={() => setPendingProd(null)}
          onConfirm={() => void switchTo(pendingProd)}
        />
      )}
    </div>
  );
}
